// src/hooks/useElementManagement.js
import { useState, useEffect } from 'react';
import { elementTypes } from '../constants/elementTypes';
import { snapToGrid } from '../utils/gridHelpers';

export const useElementManagement = () => {
  const [elements, setElements] = useState([]);
  const [currentType, setCurrentType] = useState(null);
  const gridSize = 20;

  useEffect(() => {
    const savedElements = localStorage.getItem('floorPlanElements');
    if (savedElements) {
      try {
        setElements(JSON.parse(savedElements));
      } catch (error) {
        console.error('Error loading saved elements:', error);
      }
    }
  }, []);

  const handleRotate = (id, newOrientation) => {
    setElements(elements.map(element => 
      element.id === id ? { ...element, orientation: newOrientation } : element
    ));
  };

  const handleDelete = (id) => {
    setElements(elements.filter(element => element.id !== id));
  };

  const handleSave = () => {
    localStorage.setItem('floorPlanElements', JSON.stringify(elements));
    alert('Floor plan saved successfully!');
  };

  const handleClear = () => {
    if (window.confirm('Are you sure you want to clear the floor plan?')) {
      setElements([]);
      localStorage.removeItem('floorPlanElements');
    }
  };

  const handleElementCreate = (x, y, type) => {
    const typeConfig = elementTypes.find(t => t.name === type);
    if (!typeConfig) return;

    const snappedX = snapToGrid(x, gridSize);
    const snappedY = snapToGrid(y, gridSize);

    const newElement = {
      id: Date.now(),
      type: typeConfig.name,
      category: typeConfig.category,
      x: snappedX,
      y: snappedY,
      width: typeConfig.minSize.width * gridSize,
      height: typeConfig.minSize.height * gridSize,
      properties: { ...typeConfig.properties },
      color: typeConfig.color
    };

    setElements([...elements, newElement]);
  };

  const handleElementUpdate = (x, y) => {
    setElements(elements.map((element, index) => {
      if (index === elements.length - 1) {
        const typeConfig = elementTypes.find(t => t.name === element.type);
        const width = Math.round(Math.min(
          Math.max(x - element.x, typeConfig.minSize.width * gridSize),
          typeConfig.maxSize?.width * gridSize || Infinity
        ) / gridSize) * gridSize;
        const height = Math.round(Math.min(
          Math.max(y - element.y, typeConfig.minSize.height * gridSize),
          typeConfig.maxSize?.height * gridSize || Infinity
        ) / gridSize) * gridSize;

        return { ...element, width, height };
      }
      return element;
    }));
  };

  return {
    elements,
    setElements, // Add this to expose setElements
    currentType,
    setCurrentType,
    handleRotate,
    handleDelete,
    handleSave,
    handleClear,
    handleElementCreate,
    handleElementUpdate
  };
};